import { MenuItem, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { StyledSelect } from '../StyledSelect/StyledSelect';
import { RootState } from 'FRONTEND/reducers/types';
import { selectUserTenantsWithRights } from 'FRONTEND/reducers/userSlice';

type SingleTenantSelectorProps = {
  onTenantSelected: (tenantId: string) => void;
  neededRights?: string[];
};

/**
 * @param onTenantSelected Callback function that is called when a tenant is selected, receiving the selected tenant ID as an argument.
 * Allows for selecting a single tenant from the list of tenants the user has access to.
 * See TenantSelector for multi-tenant selection.
 * @return A dropdown selector to select a single tenant. On init, pre-selects the first tenant in the user's tenant list and calls the callback with that tenant ID.
 */
const SingleTenantSelector = ({ onTenantSelected, neededRights }: SingleTenantSelectorProps) => {
  const userTenants = useSelector(selectUserTenantsWithRights(neededRights));
  const tenantEntities = useSelector((state: RootState) => state.user.tenantList);
  const [selectedTenant, setSelectedTenant] = useState(userTenants[0]);

  // On init, select tenant 0 and use the callback to notify parent component of the selected tenant.
  useEffect(() => {
    if (userTenants && userTenants.length > 0 && selectedTenant) {
      onTenantSelected(selectedTenant);
    }
  }, []);

  return (
    <StyledSelect
      value={selectedTenant}
      disabled={!userTenants || userTenants.length === 0}
      onChange={(e) => {
        setSelectedTenant(e.target.value as string);
        onTenantSelected(e.target.value as string);
      }}
      variant='outlined'
    >
      {userTenants && userTenants?.map((id) => {
        const tenant = tenantEntities[id];
        return (
          <MenuItem key={tenant?.id} value={tenant?.id} sx={{ justifyContent: 'space-between', pl: 2 }}>
            <Typography>{tenant?.name}</Typography>
          </MenuItem>
        );
      })}
    </StyledSelect>
  );
};

export default SingleTenantSelector;
